/**
 * Helper Methods for working with routes
 */
import { type SiteId } from '@solinkcorp/api-types';
import { type JWTUser } from '@solinkcorp/authentication';
import {
  type GenerateUrlParams,
  generateVideoDiscoveryURL,
  type HistogramRange,
} from '@solinkcorp/vd-common';
import { StorageAPI } from 'clients';
import { type PlayingCamera } from 'components/VideoPlayer/controller/PlayingCamerasController';
import { jwtDecode } from 'jwt-decode';
import qs from 'qs';
import { type Location } from 'react-router-dom';
import type { SetOptional } from 'type-fest';
import { DEVICE_ID, JWT, LOCATION_ID, SELECTED_LOCATION } from 'vd-constants';
import FeaturesUtil from './FeaturesUtil';

export interface LocationLike {
  hash?: string;
  pathname?: string;
  search: string;
}

/**
 * Convert a hash route into something useful
 */
export function transformHashRoute({ location }: { location: Location }): {
  pathname: string;
  search?: string;
} {
  const { hash } = location;
  if (!hash) {
    return location;
  }

  const qsIndex = hash.indexOf('?');
  if (qsIndex === -1) {
    return { pathname: hash.substr(1) };
  }
  const pathname = hash.substr(1, qsIndex - 1);
  const search = hash.substr(qsIndex);
  return { pathname, search };
}

/**
 * Get the [window] location object.
 */
export const getLocation = () => {
  return window.location;
};

/**
 * Get the current URL.
 */
export const getUrl = ({ location = window.location }: { location?: globalThis.Location } = {}) => {
  if (location && location.href) {
    return location.href;
  }
};

/**
 * Check whether there are any query string params
 */
export const hasQueryString = ({
  location = window.location,
}: { location?: globalThis.Location } = {}) => {
  return !!(location && location.search);
};

/**
 * Extract the query properties
 * @param {Location} location the location object
 * @returns {object}
 */
export function getQueryProps({
  location = window.location,
}: { location?: LocationLike } = {}): RouteQueryProps {
  if (location.hash) {
    return getQueryPropsFromHash({ location });
  }
  return getQueryPropsFromQueryString({ location });
}

/**
 * Extract the Query Props from the QueryString
 * @param {Location} location The current location
 * @param {transformResponse}
 * @returns {object}
 */
function getQueryPropsFromQueryString(
  { location }: { location?: LocationLike },
  transformResponse?: true,
): RouteQueryProps;

function getQueryPropsFromQueryString(
  { location }: { location?: LocationLike },
  transformResponse: false,
): { [key: string]: string };

function getQueryPropsFromQueryString(
  { location = window.location }: { location?: LocationLike },
  transformResponse: boolean = true,
) {
  const data = normalizeQuery(qs.parse(location.search, { ignoreQueryPrefix: true }));
  return transformResponse ? transformQueryParams(data) : data;
}

export const getAnyPresetDeviceId = (location: globalThis.Location = window.location) => {
  const queryData = getQueryPropsFromQueryString({ location }, false);
  const jwtToken = queryData[JWT];
  let deviceIdFromJWT = null;

  if (jwtToken) {
    const jwtUser = jwtDecode(jwtToken) as JWTUser;
    deviceIdFromJWT = jwtUser?.app_metadata?.deviceId;
  }

  const selectedLocation = StorageAPI.getLocally(SELECTED_LOCATION);

  return (deviceIdFromJWT ||
    queryData[DEVICE_ID] ||
    queryData[LOCATION_ID] ||
    selectedLocation ||
    undefined) as SiteId | undefined;
};

/**
 * Parse a location object using hash
 * @param {Location} location The current window.location
 * @returns {object}
 */
function getQueryPropsFromHash({ location }: { location: LocationLike }) {
  const { hash } = location;
  if (!hash) {
    return {};
  }

  const data: { [key: string]: string | undefined } = {};
  if (hash && /\?/.test(hash)) {
    const query = hash.split('?')[1];
    query.split('&').forEach((queryParam) => {
      const keyValue = queryParam.split('=');
      const key = keyValue[0];
      const value = keyValue[1];

      data[key] = value;
    });
    data.query = query;
  }

  return transformQueryParams(data);
}

export interface RouteQueryProps {
  date?: Date;
  capsules?: string;
  cameraIds?: string[];
  /** Contextine code */
  code?: string;
  eventId?: string;
  feature?: string;
  filterFavourites?: boolean;
  groupId?: string;
  hash?: string;
  locationId?: SiteId;
  siteIds?: readonly SiteId[];
  query?: string;
  range?: HistogramRange;
  reportId?: string;
  search?: string;
  showAlarms?: boolean;
  showBookmarks?: boolean;
  showMotion?: boolean;
  showTransaction?: boolean;
  showRevenue?: boolean;
  source?: string;
  streamId?: string;
  tenantId?: string;
}

/**
 * Transform the given data blob into query params
 */
function transformQueryParams(data: { [key: string]: string | undefined }): RouteQueryProps {
  const {
    appDate,
    cameraId,
    capsules,
    filterFavourites,
    histogramRange,
    siteIds,
    search,
    showBookmarks,
    showMotion,
    showTransactions,
    showAlarms,
    showRevenue,
  } = data;

  // liveViewOnly users must never load into recorded playback, so ignore any
  // `appDate` in the URL and force the player to boot into live.
  const ignoreAppDate = FeaturesUtil.isLiveViewOnly();

  return {
    ...data,
    // other params that need extra handling
    date:
      !ignoreAppDate && appDate && !isNaN(parseInt(appDate))
        ? new Date(parseInt(appDate) * 1000)
        : undefined,
    cameraIds: cameraId?.split(',') || undefined,
    capsules: _parseEncodedUri(capsules),
    filterFavourites: _parseShowFilters(filterFavourites),
    siteIds: siteIds ? (_parseEncodedUri(siteIds)?.split(',') as SiteId[]) : undefined,
    range: _parseRange(histogramRange),
    search: _parseEncodedUri(search),
    showBookmarks: _parseShowFilters(showBookmarks),
    showMotion: _parseShowFilters(showMotion),
    showTransaction: _parseShowFilters(showTransactions),
    showAlarms: _parseShowFilters(showAlarms),
    showRevenue: _parseShowFilters(showRevenue),
  };
}

function _parseEncodedUri(value: string | undefined) {
  if (value) {
    return decodeURIComponent(value.replace(/\+/g, ' '));
  }
}

function _parseRange(value: string | undefined): HistogramRange | undefined {
  if (value) {
    switch (value.toLowerCase()) {
      case 'year':
        return 'Year';
      case 'month':
        return 'Month';
      case 'week':
        return 'Week';
      case 'day':
        return 'Day';
    }
  }
  return undefined;
}

function _parseShowFilters(value: string | undefined) {
  const formattedValue = value ? value.toLowerCase() : null;
  if (formattedValue === null || formattedValue === undefined) {
    return undefined;
  }
  return formattedValue === 'yes';
}

/**
 * Normalize resuts of qs.parse.
 *
 * qs.parse can give us back a value or an array of values for a given key.
 * (e.g. "?foo=bar&foo=baz" will give us back an `{foo: ["bar", "baz"]}`)
 * This function sanitizes this data and makes sure each value is a string.
 */
function normalizeQuery(data: qs.ParsedQs) {
  for (const key of Object.keys(data)) {
    const value = data[key];
    if (Array.isArray(value)) {
      if (typeof value[0] === 'string') {
        data[key] = value[0];
      } else {
        data[key] = undefined;
      }
    } else if (typeof value !== 'string') {
      data[key] = undefined;
    }
  }

  return data as { [key: string]: string | undefined };
}

export const getRelativePath = ({ path, prefix }: { path: string; prefix: string }) =>
  path.replace(prefix, '');

/**
 * Updates URL search params by iterating through newParams obj, preserves keys not included in newParams obj
 * We omit "route" since we're only interested in updating the URL params for the current domain.com/path
 * */
export function updateURLSearchParamBy(
  newParams: SetOptional<GenerateUrlParams, 'route'>,
  currentURLSearch = window.location.search,
): URLSearchParams {
  const currentSearchParams = new URLSearchParams(currentURLSearch);

  const newSearchParams = new URLSearchParams(
    generateVideoDiscoveryURL({ route: '', ...newParams }).split('?')[1],
  );

  const updatedSearchParams = new URLSearchParams({
    ...Object.fromEntries(currentSearchParams),
    ...Object.fromEntries(newSearchParams),
  });

  return updatedSearchParams;
}

export function removeSearchParamBy(
  paramsToRemove: Array<keyof GenerateUrlParams | 'appDate'>,
  currentURLSearch = window.location.search,
) {
  const currentSearchParams = new URLSearchParams(currentURLSearch);

  paramsToRemove.forEach((param) => {
    currentSearchParams.delete(param);
  });

  return currentSearchParams;
}

export function generateCameraIdSearchParam(
  cameras: PlayingCamera[],
  isCameraGroupContext: boolean,
) {
  let cameraIdsString = '';
  cameras.forEach(({ camera: { id, deviceId } }) => {
    if (isCameraGroupContext) {
      cameraIdsString += cameraIdsString ? `,${deviceId}.${id}` : `${deviceId}.${id}`;
    } else {
      cameraIdsString += cameraIdsString ? `,${id}` : id;
    }
  });
  return cameraIdsString;
}

/**
 * if the video launch URL is for a camera group, the cameraId format is <DEVICE_ID>.<CAMERA_ID>
 * if there is more than 1 camera to be launched, a comma separated list of device and camera IDs can be passed
 * - e.g., <DEVICE_ID1>.<CAMERA_ID1>,<DEVICE_ID2><CAMERA_ID2>
 * the cameraIds actually represents array of <DEVICE_ID>.<CAMERA_ID>
 */
export function getCameraIdsFromUrlParams(
  groupId: string | undefined,
  cameraIds: string[] | undefined,
): string[] | undefined {
  if (!groupId || !cameraIds) {
    return;
  }

  return cameraIds.map((location) => location.split('.')?.[1]);
}
